import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  // Global loading state
  isLoading: false,
  
  // Specific loading states for different operations
  isSubmitting: false,
  isFetching: false,
  isUploading: false,
  isDeleting: false,
  isUpdating: false,
  
  // Loading states for specific components/pages
  pageLoading: false,
  modalLoading: false,
  buttonLoading: false,
  
  // Loading message for user feedback
  loadingMessage: '',
  
  // Loading progress (0-100) for progress indicators
  loadingProgress: 0,
  
  // Array to track multiple concurrent operations
  activeOperations: []
}

export const loaderSlice = createSlice({
  name: 'loader',
  initialState,
  reducers: {
    // Global loading actions
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    
    // Specific operation loading actions
    setSubmitting: (state, action) => {
      state.isSubmitting = action.payload
    },
    
    setFetching: (state, action) => {
      state.isFetching = action.payload
    },
    
    setUploading: (state, action) => {
      state.isUploading = action.payload
    },
    
    setDeleting: (state, action) => {
      state.isDeleting = action.payload
    },
    
    setUpdating: (state, action) => {
      state.isUpdating = action.payload
    },
    
    // Component-specific loading actions
    setPageLoading: (state, action) => {
      state.pageLoading = action.payload
    },
    
    setModalLoading: (state, action) => {
      state.modalLoading = action.payload
    },
    
    setButtonLoading: (state, action) => {
      state.buttonLoading = action.payload
    },
    
    // Loading message and progress
    setLoadingMessage: (state, action) => {
      state.loadingMessage = action.payload
    },
    
    setLoadingProgress: (state, action) => {
      state.loadingProgress = Math.max(0, Math.min(100, action.payload))
    },
    
    // Batch loading state update
    setLoadingState: (state, action) => {
      return { ...state, ...action.payload }
    },
    
    // Operation tracking for concurrent operations
    startOperation: (state, action) => {
      const { operationId, message } = action.payload
      if (!state.activeOperations.includes(operationId)) {
        state.activeOperations.push(operationId)
      }
      state.isLoading = state.activeOperations.length > 0
      if (message) {
        state.loadingMessage = message
      }
    },
    
    stopOperation: (state, action) => {
      const operationId = action.payload
      state.activeOperations = state.activeOperations.filter(id => id !== operationId)
      state.isLoading = state.activeOperations.length > 0
      if (state.activeOperations.length === 0) {
        state.loadingMessage = ''
        state.loadingProgress = 0
      }
    },
    
    // Clear all loading states
    clearAllLoading: (state) => {
      return { ...initialState }
    },
    
    // Reset specific loading states
    resetOperationLoading: (state) => {
      state.isSubmitting = false
      state.isFetching = false
      state.isUploading = false
      state.isDeleting = false
      state.isUpdating = false
    }
  }
})

// Export actions
export const {
  setLoading,
  setSubmitting,
  setFetching,
  setUploading,
  setDeleting,
  setUpdating,
  setPageLoading,
  setModalLoading,
  setButtonLoading,
  setLoadingMessage,
  setLoadingProgress,
  setLoadingState,
  startOperation,
  stopOperation,
  clearAllLoading,
  resetOperationLoading
} = loaderSlice.actions

// Export selectors for easy access to loading states
export const selectIsLoading = (state) => state.loader.isLoading
export const selectIsSubmitting = (state) => state.loader.isSubmitting
export const selectIsFetching = (state) => state.loader.isFetching
export const selectIsUploading = (state) => state.loader.isUploading
export const selectIsDeleting = (state) => state.loader.isDeleting
export const selectIsUpdating = (state) => state.loader.isUpdating
export const selectPageLoading = (state) => state.loader.pageLoading
export const selectModalLoading = (state) => state.loader.modalLoading
export const selectButtonLoading = (state) => state.loader.buttonLoading
export const selectLoadingMessage = (state) => state.loader.loadingMessage
export const selectLoadingProgress = (state) => state.loader.loadingProgress
export const selectActiveOperations = (state) => state.loader.activeOperations
export const selectHasActiveOperations = (state) => state.loader.activeOperations.length > 0

export default loaderSlice.reducer
